import { Inject, Injectable, TransferState } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  tap,
} from 'rxjs';

import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import { RequestParams } from '@src/app/shared/types/common';
import {
  GetAnnouncementResponse,
  GetBondReponse,
  GetContactResponse,
} from '@src/app/shared/types/api';
import { RoutesEnum } from '@src/app/enums';
import { Router } from '@angular/router';
import { HOMEPAGE_KEY } from '../transfer-state/keys';
import { SeoMetaService } from './seo-meta.service';

@Injectable({
  providedIn: 'root',
})
export class PagesService {
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  private homepageSubject = new BehaviorSubject<any>({});
  homepage$: Observable<any> = this.homepageSubject.asObservable();

  private brandResourcesSubject = new BehaviorSubject<Record<string, any>>({});
  brandResources$: Observable<Record<string, any>> =
    this.brandResourcesSubject.asObservable();

  private contactSubject = new BehaviorSubject<Partial<GetContactResponse>>({});
  contact$: Observable<Partial<GetContactResponse>> =
    this.contactSubject.asObservable();

  private careersContentSubject = new BehaviorSubject<Record<string, any>>({});
  careersContent$: Observable<Record<string, any>> =
    this.careersContentSubject.asObservable();

  constructor(
    @Inject(Router) private router: Router,
    private transferState: TransferState,
    private apiService: ApiService,
    private seoMetaService: SeoMetaService,
  ) {}

  get homepage(): any[] {
    return this.homepageSubject.value;
  }

  get brandResources(): Record<string, any> {
    return this.brandResourcesSubject.value;
  }

  get contact(): Partial<GetContactResponse> {
    return this.contactSubject.value;
  }

  get careersContent(): Record<string, any> {
    return this.careersContentSubject.value;
  }

  setIsLoading(value: boolean): void {
    this.isLoadingSubject.next(value);
  }

  setHomepage(data: Record<string, any>): void {
    this.homepageSubject.next(data);
  }

  setBrandResources(data: Record<string, any>): void {
    this.brandResourcesSubject.next(data);
  }

  setContact(data: GetContactResponse): void {
    this.contactSubject.next(data);
  }

  setCareersContent(data: Record<string, any>): void {
    this.careersContentSubject.next(data);
  }

  /**
   * @description Handling API call errors
   * @param {HttpErrorResponse} error
   */
  handleError(error: HttpErrorResponse): void {
    this.isLoadingSubject.next(false);

    if (
      error.error?.statusCode === 404 ||
      error.error?.status === 404 ||
      error?.status === 404
    ) {
      this.router.navigate(['/' + RoutesEnum.NOT_FOUND], {
        skipLocationChange: true,
      });
    } else if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  // getHomepage(params: RequestParams): Observable<Record<string, any>> {
  //   this.setIsLoading(true);

  //   return this.apiService
  //     .request({
  //       method: 'get',
  //       endpoint: ApiUrl.getHomePage,
  //       params,
  //     })
  //     .pipe(
  //       tap((data) => {
  //         this.transferState.set(HOMEPAGE_KEY, data);
  //         this.seoMetaService.apply(data.seo);
  //       }),
  //       catchError((error) => {
  //         console.error('getHomepage error', error);
  //         return of(null);
  //       }),
  //       finalize(() => {}),
  //     );

  //   // return this.apiService
  //   //   .request({
  //   //     method: 'get',
  //   //     endpoint: ApiUrl.getHomePage,
  //   //     params,
  //   //   })
  //   //   .pipe(
  //   //     tap((data) => {
  //   //       this.transferState.set(HOMEPAGE_KEY, data);
  //   //       this.seoMetaService.apply(data.seo);
  //   //     }),
  //   //     catchError((error) => {
  //   //       console.error('getHomepage error', error);
  //   //       return of(null);
  //   //     }),
  //   //     finalize(() => {
  //   //       this.isLoadingSubject.next(false);
  //   //     }),
  //   //   );
  // }

  getHomepage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getHomePage,
      params,
    });
  }

  getUsefulLinksPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getUsefulLinksPage,
      params,
    });
  }

  getAlienablePropertiesPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAlienablePropertiesPage,
      params,
    });
  }

  getAnnouncementsPage(
    params: RequestParams,
    options?: { withLoading?: boolean },
  ): Observable<GetAnnouncementResponse> {
    if (options?.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAnnouncementsPage,
      params,
    });
  }

  getAnnouncementsItems(
    params: RequestParams,
    options?: { withLoading?: boolean },
  ): Observable<Record<string, any>[]> {
    if (options?.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAnnouncementsItems,
      params,
    });
  }

  getAnnouncementsFilters(
    params: RequestParams,
  ): Observable<Record<string, any>[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAnnouncementsFilters,
      params,
    });
  }

  getAnnouncementBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<GetAnnouncementResponse> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAnnouncement(slug),
      params,
    });
  }

  getBrandResourcesPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBrandResourcesPage,
      params,
    });
  }

  getShareholdersInvestorsPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getShareholdersInvestorsPage,
      params,
    });
  }

  getCorporateSocialResponsibilityPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCorporateSocialResponsibilityPage,
      params,
    });
  }

  getContactPage(params: RequestParams): Observable<GetContactResponse> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getContactPage,
      params,
    });
  }

  postContactForm(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.sendContactFrom,
      params,
      body,
    });
  }

  postCareerBid(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.sendCareerBid,
      params,
      body,
      requestFormat: 'form',
    });
  }

  postCareerHrBid(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.sendCareerHrBid,
      params,
      body,
      requestFormat: 'form',
    });
  }

  postBrandResourcePasswordCheck(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.checkBrandResourcePassword,
      params,
      body,
    });
  }

  getBondPage(
    params: RequestParams,
  ): Observable<GetBondReponse & { words: any }> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBondPage,
      params,
    });
  }

  getShoppingPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getShoppingPage,
      params,
    });
  }

  getReportsPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getReportsPage,
      params,
    });
  }

  getCorporateManagementPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCorporateManagementPage,
      params,
    });
  }

  getCareersPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCareersPage,
      params,
    });
  }

  getCareerPageContent(params: RequestParams): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCareerPageContent,
      params,
    });
  }

  getCareerBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCareer(slug),
      params,
    });
  }

  getPricesPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getPricesPage,
      params,
    });
  }

  getPricesArchivePage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getPricesArchivePage,
      params,
    });
  }

  getAboutUsPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAboutUsPage,
      params,
    });
  }

  getAdviceGuidancePage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAdviceGuidancePage,
      params,
    });
  }

  getChronologyPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getChronologyPage,
      params,
    });
  }

  getCalculatorsPage(
    params: Record<string, string | number>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCalculatorsPage,
      params,
    });
  }

  getRegulations(
    params: Record<string, string | number>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getRegulations,
      params,
    });
  }

  getTermsOfServices(
    params: Record<string, string | number>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getTermsOfServices,
      params,
    });
  }

  getClientRights(
    params: Record<string, string | number>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getConsumerRights,
      params,
    });
  }

  getCompliancePage(
    params: Record<string, string | number>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCompliancePage,
      params,
    });
  }

  getTraineePage(
    params: Record<string, string | number>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getTraineePage,
      params,
    });
  }

  postTraineeApplication(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.postTraineeApplication,
      params,
      requestFormat: 'form',
      body,
    });
  }

  getSustainableDevelopmentPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getSustainableDevelopmentPage,
      params,
    });
  }
}
